// Content module API service
import { apiService } from '../../../shared/services/api';
import { Content, ContentForm } from '../types';

export class ContentService {
  // Contents CRUD
  async getContents(filters?: { waypoint_name?: string; version?: number; museum_code?: string }): Promise<Content[]> {
    return apiService.getContents(filters) as Promise<Content[]>;
  }

  async getContent(waypointName: string, version: number = 1): Promise<Content> {
    return apiService.getContent(waypointName, version) as Promise<Content>;
  }

  async createContent(data: ContentForm): Promise<Content> {
    return apiService.createContent(data) as Promise<Content>;
  }

  async updateContent(waypointName: string, version: number, data: Partial<ContentForm>): Promise<Content> {
    return apiService.updateContent(waypointName, version, data) as Promise<Content>;
  }

  async deleteContent(waypointName: string, version: number): Promise<void> {
    await apiService.deleteContent(waypointName, version);
  }

  // File management
  async uploadFile(
    file: File,
    fileType: string = 'image',
    contentId?: number,
    screen?: 'primary' | 'secondary',
  ): Promise<any> {
    return apiService.uploadContentFile(file, fileType, contentId, screen);
  }

  async deleteFile(fileKey: string): Promise<void> {
    await apiService.deleteS3File(fileKey);
  }

  async getFiles(fileType: string = 'image'): Promise<any> {
    return apiService.listS3Files(fileType);
  }

  // Utility methods
  async getContentForWaypoint(waypointName: string, version: number = 1): Promise<Content | null> {
    try {
      return await this.getContent(waypointName, version);
    } catch (error) {
      return null;
    }
  }

  async hasContentForWaypoint(waypointName: string, version: number = 1): Promise<boolean> {
    const template = await this.getContentForWaypoint(waypointName, version);
    return template !== null;
  }

  async getContentVersions(waypointName: string): Promise<Content[]> {
    return apiService.getContentVersions(waypointName) as Promise<Content[]>;
  }

  /**
   * Check if content version is being used by any tour
   */
  async checkContentUsage(
    waypointName: string,
    version: number,
  ): Promise<{
    waypoint_name: string;
    version: number;
    is_used: boolean;
    used_by_tours: Array<{
      id: number;
      tour_id: number;
      description: string;
    }>;
  }> {
    return apiService.get(`/content/contents/${waypointName}/${version}/usage/`);
  }
}

// Singleton instance
export const contentService = new ContentService();
